import { AmplitudeMemoryStorage } from './amplitudeMemoryStorage'
import { ELEMENT_CLICK_SELECTORS, shouldTrackElementClick } from './elementClickPolicy'

/**
 * Who the page's Amplitude counts the visitor as:
 *  - `consented` — the visitor accepted the Amplitude service in the cookie
 *    banner: the SDK's defaults (`AMP_*` cookie identity, `localStorage`
 *    event queue, IP-based geo), so retention and sessions span visits.
 *  - `cookieless` — no choice yet, or declined: nothing is read from or
 *    written to the device and no IP is attached. The device id is the
 *    server's per-day visitor id (`fetchVisitorDeviceId`), so one visitor
 *    clicking through the site is one user for the day. Until 2026-10-22
 *    this mode let the SDK mint an id per page load — every click was a
 *    new user (759 "new" web users on 2026-10-22 against ~32 in GA4).
 */
export type AmplitudeIdentity = { mode: 'consented' } | { mode: 'cookieless'; deviceId: string }

/**
 * `amplitude.init` options for `identity` (Browser SDK 2.11.1 option names,
 * checked against the CDN bundle `consent.ts` loads). `siteHost` is the
 * page's `location.hostname`.
 *
 * Both modes send the two inputs of Amplitude's Marketing Analytics space
 * (TASK-1171; until then the web sent no page views):
 *  - `[Amplitude] Page Viewed` on load and on every client-side route change
 *    (`history.pushState` / `popstate`). `pathOnly`: the map rewrites the
 *    query string as the visitor pans and builds routes, which is not a new
 *    page. The event carries the landing URL's UTM / click ids / referrer
 *    as event properties, read from the URL, no storage needed.
 *  - attribution — the SDK's campaign `$identify` (`utm_*`, `referrer`,
 *    `referring_domain`, click ids as user properties, plus `initial_*`).
 *    Cookieless keeps the last campaign in memory only, so each full page
 *    load is judged on its own URL and referrer, and `initial_*` is first
 *    touch of the per-day visitor, not of the person.
 *  - `[Amplitude] Element Clicked` on the site's controls, with the click
 *    position and viewport size (TASK-1173, `elementClickPolicy`) — the
 *    input of our own click heatmaps. Nothing is read from or written to
 *    the device for it, so it runs in both modes.
 *
 * The options go under `autocapture`, not `defaultTracking`: SDK 2.11.1
 * reads element interactions only from `autocapture`, and when that key is
 * present it replaces `defaultTracking` wholesale.
 */
export function amplitudeInitOptions(
  identity: AmplitudeIdentity,
  siteHost: string,
): Record<string, unknown> {
  const autocapture = {
    pageViews: { trackHistoryChanges: 'pathOnly' },
    // The SDK's default own-site exclusion comes from the cookie domain,
    // which the cookieless mode does not have — without this rule a click
    // from one prerendered page to another would count as a referral from
    // the site itself. Spelled out for both modes so they agree.
    attribution: { excludeReferrers: [ownSiteReferrer(siteHost)] },
    sessions: true,
    elementInteractions: {
      cssSelectorAllowlist: [...ELEMENT_CLICK_SELECTORS],
      shouldTrackEventResolver: shouldTrackElementClick,
    },
  }
  if (identity.mode === 'consented') return { autocapture }
  return {
    autocapture,
    deviceId: identity.deviceId,
    identityStorage: 'none',
    storageProvider: new AmplitudeMemoryStorage(),
    // The SDK takes this object as a whole, so the two fields we keep
    // must be spelled out — an omitted one reads as `false`.
    trackingOptions: { ipAddress: false, language: true, platform: true },
  }
}

/** `london-tube.com` and any subdomain of it (`www.` dropped first). */
function ownSiteReferrer(siteHost: string): RegExp {
  const domain = siteHost.replace(/^www\./, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|\\.)${domain}$`)
}
